import { listify } from "radash";

export const AccountStatus = {
  PENDING: "Pending",
  ACTIVE: "Active",
  PAUSED: "Paused",
  CLOSED: "Closed",
  DORMANT: "Dormant",
  ONBOARDING: "Onboarding",
} as const;

export const accountStatuses = computed(() => {
  return listify(AccountStatus, (key, value) => ({ value: key, label: value }));
});

export const PaymentType = {
  CARD: "Credit Card",
  ACH: "ACH (Bank Account)",
  CHECK: "Check",
} as const;

export const paymentTypes = computed(() => {
  return listify(PaymentType, (key, value) => ({ value: key, label: value }));
});

export const UserStatus = {
  Inactive: false,
  Active: true,
} as const;

export const userStatuses = computed(() => {
  return listify(UserStatus, (key, value) => ({ value: key, label: value }));
});

export const YesNo = {
  Yes: true,
  No: false,
} as const;

export const yesNoOptions = computed(() => {
  return listify(YesNo, (key, value) => ({
    value: value,
    label: key,
  }));
});

export const StatusCodes = {
  404: "Not Found",
  200: "Success",
  202: "Accepted",
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  500: "Internal Server Error",
  503: "Service Unavailable",
} as const;

export const CompanyTypes = {
  LAW_FIRM: "Law Firm",
  TITLE_AGENCY: "Title Agency",
  MORTGAGE_COMPANY: "Mortgage Company",
  INSURANCE_COMPANY: "Insurance Company",
  BANK: "Bank",
  HEALTHCARE: "Healthcare",
  SCHOOL_UNIVERSITY: "School or University",
  GOVERNMENT: "Local or Federal Government",
  REAL_ESTATE: "Real Estate Agency",
  OTHER: "Other",
} as const;

export const companyTypeOptions = computed(() => {
  return listify(CompanyTypes, (key, value) => ({ value: key, label: value }));
});

export const PaymentStatus = {
  PENDING: "Pending",
  PAID: "Paid",
  FAILED: "Failed",
  REFUNDED: "Refunded",
  OPEN: "Open",
  CANCELLED: "Cancelled",
} as const;

export const paymentStatusOptions = computed(() => {
  return listify(PaymentStatus, (key, value) => ({ value: key, label: value }));
});

export const InvoiceStatus = {
  OPEN: "Open",
  PARTIAL_PAID: "Partial Paid",
  PAID: "Paid",
  CANCELLED: "Cancelled",
  REFUNDED: "Refunded",
  WRITTEN_OFF: "Written Off",
} as const;

export const invoiceStatusOptions = computed(() => {
  return listify(InvoiceStatus, (key, value) => ({ value: key, label: value }));
});

export const NetAccounts = {
  0: "COD",
  15: "Net 15",
  30: "Net 30",
  45: "Net 45",
  60: "Net 60",
  90: "Net 90",
} as const;

export type MenuDocsOption = {
  icon: string;
  label: string;
  click: (data: any) => void;
  key: string;
  disabled?: boolean;
  isLoading?: boolean;
};

export const ItemKeysValues = {
  LOCK: "lock",
  DOWNLOAD: "download",
};

export const rowStatuses = {
  COMPLETE: "COMPLETE",
};

/**
 * This constant holds external links that are static through the app. The idea is
 * to centralize these links here.
 */
export const EXTERNAL_LINKS = {
  HELP_DESK: "https://vimeo.com/showcase/11639512/embed2",
} as const;

/**
 * Interface for toast service
 */
export interface IToastService {
  message: (options: {
    title?: string;
    message: string;
    type?: string;
    icon?: string;
  }) => void;
}

/**
 * Interface for axios instance
 */
export interface IAxiosInstance {
  get: (url: string, config?: any) => Promise<any>;
  post: (url: string, data?: any, config?: any) => Promise<any>;
  put: (url: string, data?: any, config?: any) => Promise<any>;
  delete: (url: string, config?: any) => Promise<any>;
  postForm: (url: string, data?: any, config?: any) => Promise<any>;
}

/**
 * Type for error reporter function
 */
export type ErrorReporter = (message: string, error: any) => void;

/**
 * Possible system setting keys. These match the backend values.
 */
export enum SystemSettingKeys {
  DEFAULT_RETENTION_TIME = 'default-retention-time'
}

/**
 * Default file retention type values, these are provided for a dropdown in the system.
 */
export enum DefaultFileRetentionTypes {
  NEVER_DELETE = 'never_delete',
  DAYS_90 = '90',
  DAYS_180 = '180',
  CUSTOM = 'custom'
}

/**
 * Mapping the Defaul
 */
export const DefaultFileRetentionLabelMap = {
  [DefaultFileRetentionTypes.NEVER_DELETE]: 'Never delete',
  [DefaultFileRetentionTypes.DAYS_90]: '90 days',
  [DefaultFileRetentionTypes.DAYS_180]: '180 days',
  [DefaultFileRetentionTypes.CUSTOM]: 'Custom'
} as const;

/**
 * This method returns an array with an object with the keys 'label' and 'value', which will
 * allow us to use it in dropdowns.
 */
export const fileRetentionDefaultTypesToList = computed(() => {
  return listify(DefaultFileRetentionLabelMap, (key, value) => ({ value: key, label: value }));
});
